import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { FileText, ArrowLeft } from 'lucide-react';
import { getQuestionsByExamId } from '../utils/questionAPI';
import { getSubjectById, getSubjectQuestionConfig } from '../utils/subjectsAPI';
import { getExamById } from '../utils/examAPI';
import { getLatestSubjectHistory } from '../utils/subjectHistorysAPI';

// 添加题型转换显示名称的辅助函数
const getTypeName = (type) => {
  switch(type) {
    case '1': 
    case 1:
    case '单选': return '单选题';
    
    case '2': 
    case 2:
    case '判断': return '判断题';
    
    case '3':
    case 3: 
    case '多选': return '多选题';
    
    default: return `题型${type}`;
  }
};

// 添加题型对应的样式类名的辅助函数
const getTypeStyle = (type) => {
  switch(type) {
    case '1': 
    case 1:
    case '单选': return 'bg-blue-50 text-blue-600 border-blue-200';
    
    case '2': 
    case 2:
    case '判断': return 'bg-green-50 text-green-600 border-green-200';
    
    case '3':
    case 3:
    case '多选': return 'bg-purple-50 text-purple-600 border-purple-200';
    
    default: return 'bg-gray-50 text-gray-600 border-gray-200';
  }
};

// 题型转换函数，统一题型标识
const normalizeType = (type) => {
  if (type === 1 || type === '1' || type === '单选') return '1';
  if (type === 2 || type === '2' || type === '判断') return '2';
  if (type === 3 || type === '3' || type === '多选') return '3';
  return type.toString();
};

function SubjectExamResult({ session }) {
  const { examId, subjectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [examResult, setExamResult] = useState(null);
  const [subjectQuestions, setSubjectQuestions] = useState([]);
  const [typeExpectedCounts, setTypeExpectedCounts] = useState({});
  const [passScore, setPassScore] = useState(60);  // 默认通过分数为60分
  const [questionScore, setQuestionScore] = useState(0);  // 每题分值
  const [totalScore, setTotalScore] = useState(100); // 默认总分为100分
  
  // 计算题型统计数据
  const calculateTypeStats = (questions, answers) => {
    const stats = {};
    
    questions.forEach(question => {
      const questionId = getQuestionId(question);
      const userAnswer = answers[questionId] || '';
      const correctOption = question.options.find(opt => opt.value === "1");
      const isCorrect = correctOption && userAnswer === correctOption.code;
      
      // 处理typename可能是数字或字符串的情况
      let type = normalizeType(question.typename || '未知');
      
      if (!stats[type]) {
        stats[type] = { total: 0, correct: 0 };
      }
      
      stats[type].total += 1;
      if (isCorrect) {
        stats[type].correct += 1;
      }
    });
    
    return stats;
  };
  
  // 加载科目题型配置
  const loadSubjectQuestions = useCallback(async (subjId) => {
    try {
      // 先获取科目基本信息
      const subjectData = await getSubjectById(subjId);
      
      if (subjectData) {
        // 设置通过分数线和总分
        setPassScore(subjectData.pass_score || 60);
        setTotalScore(subjectData.total_score || 100);
        
        console.log('科目信息:', {
          name: subjectData.name,
          pass_score: subjectData.pass_score,
          total_score: subjectData.total_score
        });
      }
      
      // 获取subject_question表数据
      const subjectQuestionsData = await getSubjectQuestionConfig(subjId);
      
      if (subjectQuestionsData && subjectQuestionsData.length > 0) {
        setSubjectQuestions(subjectQuestionsData);
        
        // 设置预期题目数量和每题分值
        const expectedCounts = {};
        let totalQuestionCount = 0;
        
        subjectQuestionsData.forEach(sq => {
          expectedCounts[normalizeType(sq.question_type_id)] = sq.question_count;
          totalQuestionCount += (sq.question_count || 0);
          
          // 如果有配置每题分值，使用配置的值
          if (sq.question_score) {
            setQuestionScore(sq.question_score);
          }
        });
        
        setTypeExpectedCounts(expectedCounts);
        
        // 如果没有配置每题分值，根据总分和题目总数计算
        if (questionScore === 0 && totalQuestionCount > 0 && subjectData?.total_score) {
          const calculatedScore = Math.round((subjectData.total_score / totalQuestionCount) * 100) / 100;
          setQuestionScore(calculatedScore);
          console.log('计算每题分值:', calculatedScore);
        }
      }
      else if (subjectData) {
        // 如果没有subject_question数据，尝试从subjects表获取type_count字段
        try {
          if (subjectData.type_count) {
            const typeCount = typeof subjectData.type_count === 'string'
              ? JSON.parse(subjectData.type_count)
              : subjectData.type_count;
              
            // 转换类型键为标准化格式
            const normalizedTypeCount = {};
            let totalCount = 0;
            
            Object.entries(typeCount).forEach(([type, count]) => {
              normalizedTypeCount[normalizeType(type)] = count;
              totalCount += (count || 0);
            });
            
            setTypeExpectedCounts(normalizedTypeCount);
            
            // 根据总分和题目总数计算每题分值
            if (totalCount > 0 && subjectData.total_score) {
              const calculatedScore = Math.round((subjectData.total_score / totalCount) * 100) / 100;
              setQuestionScore(calculatedScore);
              console.log('根据type_count计算每题分值:', calculatedScore);
            }
          }
        } catch (e) {
          console.error('解析type_count错误:', e);
        }
      }
    } catch (error) {
      console.error('加载科目题型配置错误:', error);
    }
  }, [questionScore]);
  
  useEffect(() => {
    // 从location state获取考试结果
    if (location.state) {
      // 确保分数与正确题数匹配
      const fixedState = { ...location.state };
      const questions = fixedState.questions || [];
      
      // 如果考卷中有答案但没有正确题数统计，计算正确题数
      if (!fixedState.correct_count && fixedState.answers) {
        let correctCount = 0;
        questions.forEach(question => {
          const questionId = getQuestionId(question);
          const userAnswer = fixedState.answers[questionId] || '';
          const correctOption = question.options.find(opt => opt.value === "1");
          if (correctOption && userAnswer === correctOption.code) {
            correctCount++;
          }
        });
        fixedState.correct_count = correctCount;
      }
      
      // 确保时间不为0
      if (!fixedState.totalTime && questions.length > 0) {
        fixedState.totalTime = 60; // 默认至少1分钟
      }
      
      setExamResult(fixedState);
      
      // 加载科目题型配置
      loadSubjectQuestions(subjectId);
      
      // 在下一个事件循环中更新分数，确保questionScore已更新
      setTimeout(() => {
        // 使用正确题数乘以每题分值计算分数
        if (questionScore > 0 && fixedState.correct_count !== undefined) {
          // 计算成绩的函数移到这里，避免依赖项问题
          const calculateScoreInner = (correctCount) => {
            // 直接使用正确题数乘以每题分值
            return Math.round(correctCount * questionScore);
          };
          
          fixedState.score = calculateScoreInner(fixedState.correct_count);
          setExamResult({...fixedState});
        }
        setLoading(false);
      }, 100);
    } else {
      // 如果没有state，从数据库加载最近的考试记录
      const fetchExamResult = async () => {
        try {
          setLoading(true);
          
          // 检查用户身份
          const userId = session?.user?.id || localStorage.getItem('user_id');
          if (!userId) {
            navigate('/login');
            return;
          }
          
          // 获取科目信息
          const subjectData = await getSubjectById(subjectId);
          if (!subjectData) throw new Error('找不到科目信息');
          
          // 设置通过分数线和总分
          setPassScore(subjectData.pass_score || 60);
          setTotalScore(subjectData.total_score || 100);
          
          console.log('科目信息:', {
            name: subjectData.name,
            pass_score: subjectData.pass_score,
            total_score: subjectData.total_score
          });
          
          // 获取考试信息
          // eslint-disable-next-line no-unused-vars
          const examData = await getExamById(examId);
          if (!examData) throw new Error('找不到考试信息');
          
          // 获取最近的考试记录 - 使用API函数替代直接数据库操作
          const historyData = await getLatestSubjectHistory(userId, examId, subjectId);
          
          if (!historyData) throw new Error('找不到考试记录');
          
          // 获取题目信息
          const allQuestions = await getQuestionsByExamId(subjectId, true);
          
          // 找出考卷中实际使用的题目
          let finalQuestions = [];
          
          if (historyData.answers && Object.keys(historyData.answers).length > 0) {
            const answerIds = Object.keys(historyData.answers);
            console.log('找到答案数量:', answerIds.length);
            
            // 根据答案ID筛选题目
            finalQuestions = allQuestions.filter(q => {
              const qId = getQuestionId(q);
              return answerIds.includes(qId.toString());
            });
            
            console.log('筛选后题目数量:', finalQuestions.length);
            
            // 如果找不到足够的题目，使用所有题目
            if (finalQuestions.length < answerIds.length / 2) {
              console.warn('找到的题目数量太少，使用所有题目');
              finalQuestions = allQuestions;
            }
          } else {
            // 如果没有答案记录，使用所有题目
            finalQuestions = allQuestions;
          }
          
          // 计算/验证正确题数
          let correctCount = historyData.correct_count || 0;
          
          // 如果正确题数是0但有分数，尝试重新计算
          if (correctCount === 0 && historyData.score > 0 && historyData.answers) {
            correctCount = 0;
            finalQuestions.forEach(question => {
              const questionId = getQuestionId(question);
              const userAnswer = historyData.answers[questionId] || '';
              const correctOption = question.options.find(opt => opt.value === "1");
              if (correctOption && userAnswer === correctOption.code) {
                correctCount++;
              }
            });
          }
          
          // 验证并修复分数和正确题数
          const totalQuestions = historyData.total_questions || finalQuestions.length;
          const fixedScore = Math.round(correctCount * questionScore); // 直接计算，不使用calculateScore
          
          // 确保有合理的用时记录 - 从数据库中没有获取到时间，使用默认值
          let totalTime = 0;
          // 根据题目数量估算用时
          if (finalQuestions.length > 0) {
            totalTime = Math.max(finalQuestions.length * 30, 60); // 默认每题30秒，至少1分钟
          }
          
          setExamResult({
            score: fixedScore,
            answers: historyData.answers || {},
            questionTimes: historyData.question_times || {},
            questions: finalQuestions,
            subject: subjectData,
            totalTime: totalTime,
            correct_count: correctCount,
            total_questions: totalQuestions
          });
          
          // 加载科目题型配置
          await loadSubjectQuestions(subjectId);
          
        } catch (error) {
          console.error('Error fetching exam result:', error.message);
        } finally {
          setLoading(false);
        }
      };
      
      fetchExamResult();
    }
  }, [location, session, examId, subjectId, navigate, loadSubjectQuestions, questionScore]);
  
  const getQuestionId = (question) => {
    return question._id || question.id;
  };
  
  const handleBackToExam = () => {
    navigate(`/exam-start/${examId}`);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // 如果没有考试结果数据
  if (!examResult) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header title="考试结果" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-5">
            <i className="fas fa-exclamation-circle text-4xl text-yellow-500 mb-4"></i>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">未找到考试结果</h2>
            <p className="text-gray-600 mb-4">无法加载该科目的考试结果数据</p>
            <button
              onClick={handleBackToExam}
              className="bg-primary text-white px-4 py-2 rounded-lg"
            >
              返回考试页面
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  const { score, questions, answers } = examResult;
  
  // 计算题型统计
  const typeStats = calculateTypeStats(questions, answers);
  
  // 判断成绩等级和是否通过考试
  let gradeColor = 'text-red-500';
  let gradeText = '不及格';
  let gradeIcon = 'fas fa-times-circle';
  const isPassed = score >= passScore;
  
  if (score >= 90) {
    gradeColor = 'text-green-500';
    gradeText = '优秀';
    gradeIcon = 'fas fa-award';
  } else if (score >= 80) {
    gradeColor = 'text-blue-500';
    gradeText = '良好';
    gradeIcon = 'fas fa-thumbs-up';
  } else if (score >= passScore) {
    gradeColor = 'text-yellow-500';
    gradeText = '及格';
    gradeIcon = 'fas fa-check-circle';
  }
  
  // 格式化题型统计，优先使用subject_question中的顺序
  const formatTypeStats = () => {
    if (subjectQuestions.length > 0) {
      // 使用subject_question表中的顺序
      return subjectQuestions.map(sq => {
        const typeId = normalizeType(sq.question_type_id);
        const typeStat = typeStats[typeId] || { total: 0, correct: 0 };
        return {
          type: typeId,
          total: typeStat.total,
          correct: typeStat.correct,
          expected: sq.question_count
        };
      });
    }
    
    // 使用typeExpectedCounts对象
    if (Object.keys(typeExpectedCounts).length > 0) {
      return Object.entries(typeExpectedCounts).map(([type, expected]) => {
        const normalizedType = normalizeType(type);
        const data = typeStats[normalizedType] || { total: 0, correct: 0 };
        return {
          type: normalizedType,
          total: data.total,
          correct: data.correct,
          expected
        };
      });
    }
    
    // 如果没有任何配置，使用typeStats的数据
    return Object.entries(typeStats).map(([type, data]) => ({
      type: normalizeType(type),
      total: data.total,
      correct: data.correct
    }));
  };
  
  const formattedTypeStats = formatTypeStats();
  
  return (
    <div className="min-h-screen flex flex-col app-bg-color">
      <Header title={examResult.subject?.name || "科目考试结果"} />
      
      <div className="flex-1 p-4">
        <div className="flex justify-between items-center mb-6">
          <button 
            onClick={handleBackToExam}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            返回考试
          </button>
        </div>
        
        {/* 成绩展示 */}
        <div className="bg-white rounded-xl shadow-md p-5 mb-4">
          <div className="flex flex-col items-center justify-center py-4">
            <div className={`text-6xl font-bold ${gradeColor} mb-2`}>
              {score}
            </div>
            <div className={`flex items-center ${gradeColor} text-xl mb-4`}>
              <i className={`${gradeIcon} mr-2`}></i>
              <span>{gradeText}</span>
            </div>
            
            {/* 添加通过分数线显示 */}
            <div className="mb-4 text-sm bg-gray-50 px-4 py-2 rounded-full">
              总分: <span className="font-medium">{totalScore}</span> | 
              通过分数线: <span className="font-medium">{passScore}</span> | 
              状态: <span className={`font-medium ${isPassed ? 'text-green-500' : 'text-red-500'}`}>
                {isPassed ? '通过' : '未通过'}
              </span>
            </div>
            
            <div className="w-full max-w-sm bg-gray-100 rounded-full h-2.5 mb-4">
              <div 
                className={`h-2.5 rounded-full ${isPassed ? 'bg-green-500' : 'bg-red-500'}`}
                style={{ width: `${(score / totalScore) * 100}%` }}
              ></div>
            </div>
            
            <div className="flex justify-center space-x-6 text-sm text-gray-500">
              <div className="flex flex-col items-center">
                <div className="font-medium text-gray-700 text-lg">
                  {examResult.correct_count || 0}/{questions.length}
                </div>
                <div>正确题数</div>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="font-medium text-gray-700 text-lg">
                  {examResult.totalTime ? Math.floor(examResult.totalTime / 60) || 1 : '-'}分钟
                </div>
                <div>用时</div>
              </div>
              
              {/* 添加每题分值显示 */}
              <div className="flex flex-col items-center">
                <div className="font-medium text-gray-700 text-lg">
                  {questionScore > 0 ? questionScore : '-'}分
                </div>
                <div>每题分值</div>
              </div>
            </div>
          </div>
          
          {/* 添加题型统计部分 */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <h4 className="text-base font-medium text-gray-700 mb-3">题型统计</h4>
            <div className="grid grid-cols-2 gap-3">
              {formattedTypeStats.map((stat) => (
                <div key={stat.type} className={`p-3 rounded-lg border ${getTypeStyle(stat.type)}`}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium">{getTypeName(stat.type)}</span>
                    <span className="text-sm">
                      {stat.correct}/{stat.total} 正确
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-current"
                      style={{ 
                        width: `${stat.total > 0 ? (stat.correct / stat.total) * 100 : 0}%`,
                        color: stat.type === '1' ? '#3b82f6' : 
                               stat.type === '2' ? '#10b981' :
                               stat.type === '3' ? '#8b5cf6' : '#6b7280'
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <div>得分: {Math.round(stat.correct * questionScore)}分</div>
                    <div>正确率: {stat.total > 0 ? Math.round((stat.correct / stat.total) * 100) : 0}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* 底部操作栏 */}
        <div className="flex flex-col space-y-3 mb-6">
          <button
            onClick={() => navigate(`/exam-answer-detail/${examId}/${subjectId}`, {
              state: {
                questions,
                answers,
                subject: examResult.subject,
                score,
                totalTime: examResult.totalTime,
                correct_count: examResult.correct_count,
                passScore: passScore, // 传递通过分数线到答题详情页
                questionScore: questionScore, // 传递每题分值到答题详情页
                totalScore: totalScore // 传递总分到答题详情页
              }
            })}
            className="flex items-center justify-center bg-white text-primary py-3 rounded-xl font-medium border border-primary"
          >
            <FileText className="w-5 h-5 mr-2" />
            查看答题详情
          </button>
          
          <button
            onClick={handleBackToExam}
            className="bg-primary text-white py-3 rounded-xl font-medium"
          >
            返回科目列表
          </button>
        </div>
      </div>
    </div>
  );
}

export default SubjectExamResult;